// src/App.js
import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { auth, db } from './firebase';
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, setDoc, getDoc, collection, addDoc, serverTimestamp } from "firebase/firestore";
import { motion, AnimatePresence } from "framer-motion";
import Login from './Login';
import Contact from './Contact';
import Terms from './Terms';
import SubscriptionOverlay from './SubscriptionOverlay';
import './App.css';
import { FaSearch, FaSignOutAlt, FaBroom, FaSortAmountDown, FaMoneyBill } from 'react-icons/fa';
import logo from './logo.jpg';

function App() {
  const [user, setUser] = useState(null);
  const [data, setData] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedFFP, setSelectedFFP] = useState("");
  const [selectedAirline, setSelectedAirline] = useState("");
  const [results, setResults] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [sortByBusiness, setSortByBusiness] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showOverlay, setShowOverlay] = useState(true);
  const [isSDKLoaded, setIsSDKLoaded] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const loadCashfreeSDK = () => {
      if (window.Cashfree) {
        setIsSDKLoaded(true);
        return;
      }
  
      const script = document.createElement('script');
      script.src = "https://sdk.cashfree.com/js/ui/1.0.26/dropin.min.js";
      script.async = true;
      script.onload = () => {
        console.log("Cashfree SDK successfully loaded");
        setIsSDKLoaded(true);
      };
      script.onerror = () => {
        console.error("Failed to load Cashfree SDK");
        setIsSDKLoaded(false);
      };
      document.body.appendChild(script);
    };
  
    loadCashfreeSDK();
    
    return () => {
      const existingScript = document.querySelector('script[src*="cashfree"]');
      if (existingScript && existingScript.parentNode) {
        existingScript.parentNode.removeChild(existingScript);
      }
    };
  }, []);
  

  useEffect(() => {
    fetch("https://docs.google.com/spreadsheets/d/e/2PACX-1vQiOAuH7BUXfxTeGkY3NZ6gEdwC5_oTpOMzUCVvToezza47IXehoxDiiJkbXffOFNjgMCpkN15Sdnnf/pub?gid=1229706843&single=true&output=csv")
      .then(res => res.text())
      .then(text => {
        const lines = text.split("\n");
        const headers = lines[0].split(",").map(h => h.trim());
        const entries = lines.slice(1).map(line => {
          const values = line.split(",");
          const entry = {};
          headers.forEach((h, i) => entry[h] = values[i]?.trim());
          return entry;
        });
        setData(entries);
      });
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      setIsLoading(true);
      if (currentUser) {
        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          setSubscription(userData.subscription || null);
          const lastSearch = userData.lastSearch || {};
          setSelectedCountry(lastSearch.country || "");
          setSelectedFFP(lastSearch.ffp || "");
          setSelectedAirline(lastSearch.airline || "");
          setRecentSearches(userData.recentSearches || []);
        }
        if (location.pathname === "/login") {
          navigate("/", { replace: true });
        }
      }
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, [location.pathname, navigate]);

  const handleLogout = () => signOut(auth);

  const isCountryAllowed = (country) => {
    if (!subscription) return false;
    if (subscription.countriesAllowed === 'unlimited') return true;
    const all = [...new Set(data.map(e => e.Country))].sort();
    return all.slice(0, subscription.countriesAllowed).includes(country);
  };

  const checkSDKLoaded = async (retries = 10, delay = 600) => {
    for (let i = 0; i < retries; i++) {
      if (window.Cashfree) {
        console.log("Cashfree SDK detected after retry", i+1);
        return true;
      }
      await new Promise(resolve => setTimeout(resolve, delay));
    }
    console.error("Cashfree SDK still not loaded after retries.");
    return false;
  };
  

  const handlePayment = async () => {
    if (!user) {
      alert("Please login first.");
      return;
    }

    try {
      const sdkLoaded = await checkSDKLoaded();
      if (!sdkLoaded) {
        throw new Error("Payment system still loading. Please try again.");
      }

      if (!navigator.onLine) {
        throw new Error("No internet connection. Check your network.");
      }

      const orderId = `order_${Date.now()}`;
      const response = await fetch("https://sandbox-api.cashfree.com/pg/orders", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-client-id": "TEST10553793f7cfd19e10285e0b4e5a39735501",
          "x-client-secret": "cfsk_ma_test_47c9601c4814144ea815987753984976_b937f409",
          "x-api-version": "2023-08-01"
        },
        body: JSON.stringify({
          order_amount: 999,
          order_currency: "INR",
          order_id: orderId,
          customer_details: {
            customer_id: user.uid,
            customer_email: user.email,
            customer_phone: "9999999999"
          }
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Payment failed: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      if (!data.payment_session_id) {
        throw new Error("Invalid response from payment gateway");
      }

      const cashfree = window.Cashfree();
      cashfree.initialiseDropin({
        paymentSessionId: data.payment_session_id,
        returnUrl: window.location.href,
        theme: "light",
        components: ["card", "netbanking", "wallet"],
        onSuccess: (data) => {
          alert("Payment successful! Thank you.");
          console.log("Payment Success:", data);
        },
        onFailure: (data) => {
          alert("Payment failed. Please try again.");
          console.error("Payment Failed:", data);
        },
        onError: (err) => {
          console.error("Payment Error:", err);
          alert("Error processing payment.");
        }
      });

    } catch (error) {
      console.error("Payment Error:", error);
      alert(error.message.includes("Failed to fetch") 
        ? "Network error. Check connection and try again." 
        : error.message);
    }
  };

  const handleSearch = async () => {
    if (!subscription) {
      alert('Subscribe to search.');
      return;
    }
    if (!selectedCountry || !isCountryAllowed(selectedCountry)) {
      alert(`Access denied for ${selectedCountry}. Upgrade your plan.`);
      return;
    }

    const filtered = data.filter(e =>
      e.Country === selectedCountry &&
      (!selectedFFP || e.FFP === selectedFFP) &&
      (!selectedAirline || e.Airline === selectedAirline)
    );

    const point = sortByBusiness ? "Business Points" : "Economy Points";
    const tax = sortByBusiness ? "Business Taxes" : "Economy Taxes";

    const sorted = filtered.sort((a, b) => {
      const aSum = (parseInt(a[point]) || 0) + (parseInt(a[tax]) || 0);
      const bSum = (parseInt(b[point]) || 0) + (parseInt(b[tax]) || 0);
      return aSum - bSum;
    });

    setResults(sorted);
    const recent = [`${selectedCountry}|${selectedFFP}|${selectedAirline}`, ...recentSearches];
    const uniqueRecent = [...new Set(recent)].slice(0, 5);
    setRecentSearches(uniqueRecent);

    if (user) {
      await setDoc(doc(db, "users", user.uid), {
        email: user.email,
        lastSearch: { country: selectedCountry, ffp: selectedFFP, airline: selectedAirline },
        recentSearches: uniqueRecent
      }, { merge: true });
    }
  };

  const clearLastSearch = () => {
    setSelectedCountry("");
    setSelectedFFP("");
    setSelectedAirline("");
    setResults([]);
    setRecentSearches([]);
    if (user) setDoc(doc(db, "users", user.uid), { lastSearch: {}, recentSearches: [] }, { merge: true });
  };

  const toggleSort = () => {
    setSortByBusiness(prev => !prev);
    if (selectedCountry) handleSearch();
  };

  const countries = [...new Set(data.map(e => e.Country))].sort();
  const ffpOptions = [...new Set(data.map(e => e.FFP))].sort();
  const airlineOptions = [...new Set(data.map(e => e.Airline))].sort();

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/" element={
        !user ? (
          <Navigate to="/login" replace />
        ) : (
          <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 relative p-6 flex items-center justify-center">
            <motion.div className="w-full max-w-4xl bg-white rounded-xl shadow-2xl p-6">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-4">
                  <img src={logo} alt="logo" className="w-10 h-10 rounded shadow-md object-cover" />
                  <div>
                    <h1 className="text-xl font-bold text-indigo-800">Miles2Go</h1>
                    <p className="text-sm text-indigo-500">Travel Smarter with Points</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {subscription && (
                    <span className="bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full">{subscription.planName}</span>
                  )}
                  <button 
                    onClick={handlePayment} 
                    className={`bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded flex gap-1 items-center text-sm ${
                      !isSDKLoaded ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    disabled={!isSDKLoaded}
                  >
                    <FaMoneyBill /> 
                    {isSDKLoaded ? "Subscribe / Upgrade" : "Loading Payment..."}
                  </button>
                  <button onClick={handleLogout} className="text-sm text-red-500 hover:underline flex items-center gap-1">
                    <FaSignOutAlt /> Logout
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <select value={selectedCountry} onChange={(e) => setSelectedCountry(e.target.value)}
                  className="border p-3 rounded-lg focus:ring-2 focus:ring-indigo-500">
                  <option value="">Select Country</option>
                  {countries.map((c, i) => (
                    <option key={i} value={c}>{c} {!subscription && "🔒"}</option>
                  ))}
                </select>

                <select value={selectedFFP} onChange={(e) => setSelectedFFP(e.target.value)}
                  className="border p-3 rounded-lg focus:ring-2 focus:ring-indigo-500">
                  <option value="">All FFPs</option>
                  {ffpOptions.map((f, i) => <option key={i} value={f}>{f}</option>)}
                </select>

                <select value={selectedAirline} onChange={(e) => setSelectedAirline(e.target.value)}
                  className="border p-3 rounded-lg focus:ring-2 focus:ring-indigo-500">
                  <option value="">All Airlines</option>
                  {airlineOptions.map((a, i) => <option key={i} value={a}>{a}</option>)}
                </select>

                <button
                  onClick={handleSearch}
                  disabled={!subscription}
                  className={`px-4 py-3 text-white font-semibold rounded-lg ${subscription ? "bg-indigo-600 hover:bg-indigo-700" : "bg-gray-400 cursor-not-allowed"
                    }`}
                >
                  <FaSearch className="inline mr-2" />
                  {subscription ? "Search" : "Subscribe to Search"}
                </button>
              </div>

              <div className="mb-4 flex justify-center gap-4 text-sm">
                <button onClick={toggleSort} className="text-purple-700 hover:underline flex items-center gap-1">
                  <FaSortAmountDown /> Sort by {sortByBusiness ? "Business" : "Economy"}
                </button>
                <button onClick={clearLastSearch} className="text-red-500 hover:underline flex items-center gap-1">
                  <FaBroom /> Clear
                </button>
              </div>

              {results.length > 0 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {results.map((entry, index) => (
                    <div key={index} className="bg-blue-50 p-4 rounded-lg shadow border border-blue-200">
                      <p><strong>From:</strong> {entry.Origin} → <strong>{entry.Destination}</strong></p>
                      <p><strong>FFP:</strong> {entry.FFP} | <strong>Airline:</strong> {entry.Airline}</p>
                      <p><strong>Economy:</strong> {entry["Economy Points"]} pts + ₹{entry["Economy Taxes"]}</p>
                      <p><strong>Business:</strong> {entry["Business Points"]} pts + ₹{entry["Business Taxes"]}</p>
                    </div>
                  ))}
                </motion.div>
              )}

              <AnimatePresence>
                {!subscription && showOverlay && (
                  <SubscriptionOverlay
                    onUnlockClick={handlePayment}
                    onClose={() => setShowOverlay(false)}
                  />
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        )
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;